export type TVPlatform = 'androidtv' | 'tizen' | 'webos' | 'web'

interface TizenApplication {
  exit?: () => void
}

interface TizenApi {
  application?: {
    getCurrentApplication?: () => TizenApplication | undefined
  }
}

interface WebOsApi {
  platformBack?: () => void
}

declare global {
  interface Window {
    tizen?: TizenApi
    webOS?: WebOsApi
  }
}

/**
 * Remote key codes. Samsung and LG send their own codes and leave
 * `KeyboardEvent.key` empty for them, so they have to be matched on `keyCode`.
 */
export const TV_KEYS = {
  SAMSUNG_RETURN: 10009,
  SAMSUNG_EXIT: 10182,
  LG_BACK: 461,
  ESCAPE: 27,
}

export const isTVBackKey = (event: KeyboardEvent): boolean =>
  event.keyCode === TV_KEYS.SAMSUNG_RETURN ||
  event.keyCode === TV_KEYS.SAMSUNG_EXIT ||
  event.keyCode === TV_KEYS.LG_BACK ||
  event.keyCode === TV_KEYS.ESCAPE ||
  event.key === 'Escape' ||
  event.key === 'GoBack'

// ---- Shell bridge ----
//
// The Tizen/webOS packages are local widgets that embed this page in an iframe.
// They have to stay local because the vendor device APIs (`window.tizen`,
// `window.webOS`) are only injected into same-origin packaged pages - a
// top-level redirect to app.leaguestar.com loses them, which is why the
// Samsung Return key used to do nothing. The shell tells us who it is via a
// `?shell=` query parameter, and we talk back to it with postMessage.

export const SHELL_MESSAGE_SOURCE = 'leaguestar-tv'

const SHELL_STORAGE_KEY = 'leaguestar_tv_shell'

const readShellPlatform = (): TVPlatform | null => {
  let shell: string | null = null
  try {
    shell = new URLSearchParams(window.location.search).get('shell')
    if (shell) {
      sessionStorage.setItem(SHELL_STORAGE_KEY, shell)
    } else {
      shell = sessionStorage.getItem(SHELL_STORAGE_KEY)
    }
  } catch {
    // sessionStorage can be unavailable inside a third-party iframe
  }
  return shell === 'tizen' || shell === 'webos' || shell === 'androidtv'
    ? shell
    : null
}

const shellPlatform = readShellPlatform()

export const isInTVShell = (): boolean => {
  try {
    return shellPlatform !== null && window.parent !== window
  } catch {
    return false
  }
}

const postToShell = (
  type: string,
  payload?: Record<string, unknown>
): boolean => {
  if (!isInTVShell()) return false
  try {
    window.parent.postMessage(
      { source: SHELL_MESSAGE_SOURCE, type, ...payload },
      '*'
    )
    return true
  } catch {
    return false
  }
}

/** Tells the shell the page is alive so it can drop its own loading screen. */
export const notifyShellReady = (): void => {
  postToShell('ready')
}

/**
 * Pings the shell while this page is running. If the pings stop the shell
 * knows the hosted page can no longer forward the Return key and takes over.
 * Returns a cleanup function.
 */
export const startShellHeartbeat = (intervalMs = 5000): (() => void) => {
  if (!isInTVShell()) return () => {}
  const id = setInterval(() => postToShell('heartbeat'), intervalMs)
  return () => clearInterval(id)
}

export const detectTVPlatform = (): TVPlatform => {
  if (shellPlatform) return shellPlatform

  const ua = navigator.userAgent.toLowerCase()

  if (window.tizen || ua.includes('tizen')) return 'tizen'
  if (window.webOS || ua.includes('web0s') || ua.includes('webos'))
    return 'webos'
  if (
    ua.includes('android') &&
    (ua.includes('tv') || window.innerWidth >= 1920)
  )
    return 'androidtv'

  return 'web'
}

export const isTVPlatform = (): boolean => {
  return detectTVPlatform() !== 'web'
}

/**
 * Hands a remote key to the packaged shell hosting this page.
 *
 * The shell keeps keyboard focus for itself, so it normally sees Return/Exit
 * first and this never runs. It stays as a fallback for the case where the
 * frame ends up focused anyway - the shell owns the Return key policy (an exit
 * confirmation on the home screen), so it decides what the key means rather
 * than being told to exit.
 *
 * Returns false when there is no shell, so the caller can fall back.
 */
export const forwardTVKeyToShell = (event: KeyboardEvent): boolean =>
  postToShell('key', { keyCode: event.keyCode })

/**
 * Closes the TV app. Samsung and LG both require Return/Exit to close the app
 * when there is no screen to go back to. Returns false when no exit path is
 * available (plain browser), so the caller can fall back to normal navigation.
 */
export const exitTVApp = (): boolean => {
  // Packaged shell hosting us in an iframe - it owns the vendor exit API.
  if (postToShell('exit')) return true

  try {
    const app = window.tizen?.application?.getCurrentApplication?.()
    if (app?.exit) {
      app.exit()
      return true
    }
  } catch {
    // Tizen API missing or not permitted
  }

  try {
    if (window.webOS?.platformBack) {
      window.webOS.platformBack()
      return true
    }
  } catch {
    // webOS API missing
  }

  return false
}
